import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import type { ActivitySource, ActivityViewData } from "../types";
import { providerRegistry } from "./providers/registry";
import { ActivityAudienceMode } from "./providers/types";
import type { ActivityEventKind, FirestoreActivityEvent } from "./providers/types";

/**
 * ActivitySource backed by the `activity_events` Firestore collection.
 *
 * Subscribes to activity events for a workspace and delegates conversion
 * and enrichment to kind-specific providers (see providers/ directory).
 */
export class ActivityEventSource implements ActivitySource {
    private workspaceId: string;
    private userId: string;
    private database: DatabaseAdapter;
    private kinds?: ActivityEventKind[];

    constructor(
        workspaceId: string,
        userId: string,
        database: DatabaseAdapter,
        kinds?: ActivityEventKind[]
    ) {
        this.workspaceId = workspaceId;
        this.userId = userId;
        this.database = database;
        this.kinds = kinds;
    }

    subscribe(callback: (activities: ActivityViewData[]) => void): () => void {
        if (!this.workspaceId) {
            callback([]);
            return () => {};
        }

        let events: FirestoreActivityEvent[] = [];
        const enrichmentData = new Map<string, unknown>();
        const enrichmentUnsubs = new Map<string, () => void>();
        const { userId, database } = this;

        const emit = () => {
            const activities: ActivityViewData[] = [];
            for (const event of events) {
                const provider = providerRegistry[event.kind];
                if (!provider) continue;
                activities.push(provider.convert(event, enrichmentData.get(event.id)));
            }
            callback(activities);
        };

        const syncEnrichmentListeners = () => {
            const activeEventIds = new Set(events.map((e) => e.id));

            for (const [eventId, unsub] of enrichmentUnsubs) {
                if (!activeEventIds.has(eventId)) {
                    unsub();
                    enrichmentUnsubs.delete(eventId);
                    enrichmentData.delete(eventId);
                }
            }

            for (const event of events) {
                if (enrichmentUnsubs.has(event.id)) continue;
                const provider = providerRegistry[event.kind];
                if (!provider?.subscribeToEnrichment) continue;

                const unsub = provider.subscribeToEnrichment(
                    event,
                    (data) => {
                        if (data !== undefined) {
                            enrichmentData.set(event.id, data);
                        } else {
                            enrichmentData.delete(event.id);
                        }
                        emit();
                    },
                    database
                );
                enrichmentUnsubs.set(event.id, unsub);
            }
        };

        const whereFilters: [string, string, any][] = [["owner", "==", this.workspaceId]];
        if (this.kinds?.length) {
            whereFilters.push(["kind", "in", this.kinds]);
        }

        const eventsUnsub = database.onQuerySnapshot(
            "activity_events",
            { where: whereFilters, orderBy: ["createdAt", "desc"], limit: 50 },
            (docs) => {
                events = [];
                for (const doc of docs) {
                    const data = doc.data();
                    if (!data) continue;
                    const event = { ...data, id: doc.id } as FirestoreActivityEvent;

                    if (event.audience.mode === ActivityAudienceMode.Users) {
                        if (!userId || !event.audience.targets?.includes(userId)) continue;
                    } else if (event.audience.mode !== ActivityAudienceMode.All) {
                        continue;
                    }

                    events.push(event);
                }
                syncEnrichmentListeners();
                emit();
            },
            (error: any) => {
                // eslint-disable-next-line no-console
                console.error("ActivityEventSource onSnapshot error:", error);
                callback([]);
            }
        );

        return () => {
            eventsUnsub();
            for (const unsub of enrichmentUnsubs.values()) unsub();
            enrichmentUnsubs.clear();
            enrichmentData.clear();
        };
    }
}
